/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { Analytics, AnalyticsCallOptions } from "firebase/analytics";
import React, { useContext } from "react";

export interface FirebaseEventParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  method?: string | undefined;
}

export interface AnalyticsEvent {
  /** Canonical event name. Sent to Firebase and used as GTM `event` field. */
  name: string;
  /** Optional GA category. If omitted, GA receives `general` as category. */
  gaCategory?: string;
  /** Optional GA action override. Defaults to `name`. */
  gaAction?: string;
  /** Optional GA label. */
  gaLabel?: string;
  /** Firebase event params; also merged into the GTM dataLayer payload. */
  params?: Record<string, unknown>;
  /** GTM ecommerce block. The provider handles the `ecommerce: null` reset. */
  ecommerce?: Record<string, unknown>;
}

interface IAnalyticsContext {
  firebaseAnalytics?: Analytics;
  isCookiesAccepted: boolean;
  enableAnalytics?: () => void;
  /** Unified facade — fans out to Firebase + GA + GTM. Use this by default. */
  trackEvent: (event: AnalyticsEvent) => void;
  /** Direct Firebase Analytics primitive. */
  trackFirebaseEvent: (
    eventName: string,
    eventParams?: FirebaseEventParams | undefined,
    options?: AnalyticsCallOptions | undefined,
  ) => void;
  /** Direct ReactGA primitive. */
  trackGAEvent: (category: string, action: string, label?: string) => void;
  /** Direct GTM dataLayer primitive. */
  trackGTMEvent: (payload: Record<string, unknown>) => void;
  trackPageView: () => void;
}

export const AnalyticsContext = React.createContext<IAnalyticsContext>(
  {} as IAnalyticsContext,
);

export const useAnalytics = (): IAnalyticsContext => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error("Component beyond AnalyticsContext");
  }

  return context;
};
